import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { get } from "lib/api";
import Icon from "@mui/material/Icon";
import brandLogo from "assets/images/logos/Logo Use this.png";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function ValveBodyDetailPublic() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useStorefrontCopy();
  const brandName = t("common.brandFallback", "Storefront");
  const BRAND = { black: "#000000", red: "#D7261E", orange: "#FF6A00" };
  const isMobileRoute = location.pathname.startsWith("/m/valve-bodies/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  // Fetch valve body details
  const { data, isLoading, error } = useQuery({
    queryKey: ["valve-body-public", documentId],
    queryFn: () =>
      get(`/api/valve-bodies/${documentId}`, {
        populate: {
          photos: true,
          finishedGood: {
            populate: {
              images: true,
            },
          },
          workOrder: true,
        },
      }),
    enabled: !!documentId,
    staleTime: 30000,
  });

  const valveBody = data?.data
    ? data.data.attributes
      ? {
          id: data.data.id,
          documentId: data.data.documentId || data.data.id,
          ...data.data.attributes,
        }
      : { ...data.data, documentId: data.data.documentId || data.data.id }
    : null;

  const finishedGood = valveBody?.finishedGood?.data?.attributes
    ? {
        id: valveBody.finishedGood.data.id,
        documentId:
          valveBody.finishedGood.data.documentId ||
          valveBody.finishedGood.data.id,
        ...valveBody.finishedGood.data.attributes,
      }
    : valveBody?.finishedGood?.attributes
    ? {
        id: valveBody.finishedGood.id,
        documentId:
          valveBody.finishedGood.documentId || valveBody.finishedGood.id,
        ...valveBody.finishedGood.attributes,
      }
    : valveBody?.finishedGood
    ? {
        ...valveBody.finishedGood,
        documentId:
          valveBody.finishedGood.documentId || valveBody.finishedGood.id,
      }
    : null;

  // Get retail price from finished good
  const retailPrice =
    finishedGood?.priceLevels?.retail || finishedGood?.unitPrice || null;

  // Get images - prefer valve body photos, fallback to finished good images
  const valveBodyImages = valveBody?.photos?.data || [];
  const finishedGoodImages = finishedGood?.images?.data || [];
  const images =
    valveBodyImages.length > 0 ? valveBodyImages : finishedGoodImages;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const selectedImage = images[selectedImageIndex]?.attributes?.url || null;

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {!isMobileRoute ? (
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#111111",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Toolbar>
              <MDBox display="flex" alignItems="center" flexGrow={1}>
                <MDBox
                  component="img"
                  src={brandLogo}
                  alt={brandName}
                  sx={{
                    height: 130,
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(toPath("/"))}
                />
              </MDBox>
            </Toolbar>
          </AppBar>
        ) : null}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <MDBox textAlign="center">
            <MDTypography sx={{ color: "#333333", fontSize: "1rem" }}>
              Loading product details...
            </MDTypography>
          </MDBox>
        </Container>
      </Box>
    );
  }

  if (error || !valveBody) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {!isMobileRoute ? (
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#111111",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Toolbar>
              <MDBox display="flex" alignItems="center" flexGrow={1}>
                <MDBox
                  component="img"
                  src={brandLogo}
                  alt={brandName}
                  sx={{
                    height: 130,
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(toPath("/"))}
                />
              </MDBox>
            </Toolbar>
          </AppBar>
        ) : null}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <MDBox textAlign="center">
            <MDTypography
              variant="h6"
              sx={{
                color: "#000000",
                fontSize: "1.25rem",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              Valve body not found
            </MDTypography>
            <MDButton
              variant="contained"
              sx={{
                bgcolor: BRAND.orange,
                color: "#ffffff",
                "&:hover": { bgcolor: "#E05D00" },
              }}
              onClick={() => navigate(toPath("/catalog"))}
            >
              Back to Catalog
            </MDButton>
          </MDBox>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Navigation Bar */}
      {!isMobileRoute ? (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.92)",
            color: "#111111",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Toolbar>
            <MDBox display="flex" alignItems="center" flexGrow={1}>
              <MDBox
                component="img"
                src={brandLogo}
                alt={brandName}
                sx={{
                  height: 130,
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
                onClick={() => navigate(toPath("/"))}
              />
            </MDBox>
            <MDBox display="flex" gap={2}>
              <MDButton
                variant="text"
                sx={{
                  color: "#111111",
                  fontSize: "0.9375rem",
                  fontWeight: "medium",
                  letterSpacing: "0.01em",
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.06)" },
                }}
                onClick={() => navigate(toPath("/catalog"))}
              >
                Catalog
              </MDButton>
              <MDButton
                variant="text"
                sx={{
                  color: "#111111",
                  fontSize: "0.9375rem",
                  fontWeight: "medium",
                  letterSpacing: "0.01em",
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.06)" },
                }}
                onClick={() => navigate("/")}
              >
                Home
              </MDButton>
              <MDButton
                variant="contained"
                sx={{
                  bgcolor: BRAND.red,
                  color: "#ffffff",
                  fontSize: "0.9375rem",
                  fontWeight: "bold",
                  letterSpacing: "0.02em",
                  "&:hover": { bgcolor: "#B71C1C" },
                }}
                onClick={() => navigate("/authentication/sign-in")}
              >
                Sign In
              </MDButton>
            </MDBox>
          </Toolbar>
        </AppBar>
      ) : null}

      {/* Valve Body Detail */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <MDButton
          variant="text"
          startIcon={<Icon>arrow_back</Icon>}
          onClick={() => navigate("/catalog")}
          sx={{ mb: 3, color: BRAND.orange }}
        >
          Back to Catalog
        </MDButton>

        <Grid container spacing={4}>
          {/* Images Gallery */}
          <Grid item xs={12} md={6}>
            {/* Main Image Display */}
            <Card
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#ffffff",
              }}
            >
              {selectedImage ? (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    bgcolor: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={selectedImage}
                    alt={finishedGood?.name || "Valve Body"}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      p: 2,
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    bgcolor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ fontSize: 128, color: "#cccccc" }}>
                    precision_manufacturing
                  </Icon>
                </Box>
              )}
            </Card>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <Grid container spacing={1.5}>
                {images.map((img, idx) => {
                  const imgUrl = img.attributes?.url || null;
                  const isSelected = idx === selectedImageIndex;
                  return (
                    <Grid item xs={3} key={idx}>
                      <Card
                        onClick={() => setSelectedImageIndex(idx)}
                        sx={{
                          cursor: "pointer",
                          border: isSelected
                            ? `3px solid ${BRAND.orange}`
                            : "2px solid transparent",
                          borderRadius: 1,
                          overflow: "hidden",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: isSelected ? BRAND.orange : BRAND.red,
                            transform: "scale(1.05)",
                          },
                          bgcolor: "#ffffff",
                        }}
                      >
                        {imgUrl ? (
                          <Box
                            component="img"
                            src={imgUrl}
                            alt={`${finishedGood?.name || "Valve Body"} ${
                              idx + 1
                            }`}
                            sx={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              bgcolor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon sx={{ fontSize: 24, color: "#cccccc" }}>
                              image
                            </Icon>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, position: "sticky", top: 100 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="start"
                mb={2}
              >
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                    flex: 1,
                    mr: 2,
                  }}
                >
                  {finishedGood?.name || "Valve Body"}
                </MDTypography>
                <Chip
                  label="Available"
                  sx={{
                    bgcolor: "#eb4b33",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    letterSpacing: "0.02em",
                    flexShrink: 0,
                  }}
                />
              </MDBox>

              <MDBox mb={3}>
                <MDTypography
                  variant="body2"
                  mb={1}
                  sx={{
                    color: "#666666",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    fontFamily: "monospace",
                  }}
                >
                  Serial Number: {valveBody.serialNumber}
                </MDTypography>
                {finishedGood?.sku && (
                  <MDTypography
                    variant="body2"
                    mb={1}
                    sx={{
                      color: "#666666",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                      fontFamily: "monospace",
                    }}
                  >
                    SKU: {finishedGood.sku}
                  </MDTypography>
                )}
                {valveBody.oem && (
                  <MDTypography
                    variant="body2"
                    mb={1}
                    sx={{
                      color: "#666666",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    OEM: {valveBody.oem}
                  </MDTypography>
                )}
                {valveBody.transmissionModel && (
                  <MDTypography
                    variant="body2"
                    mb={1}
                    sx={{
                      color: "#666666",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    Transmission Model: {valveBody.transmissionModel}
                  </MDTypography>
                )}
              </MDBox>

              {retailPrice && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                      color: BRAND.orange,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                    }}
                  >
                    ${parseFloat(retailPrice).toFixed(2)}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "#666666",
                      fontSize: "0.75rem",
                    }}
                  >
                    Retail Price
                  </MDTypography>
                  {finishedGood?.coreCharge && (
                    <MDTypography
                      variant="body2"
                      sx={{
                        color: "#eb4b33",
                        fontSize: "0.875rem",
                        mt: 1,
                      }}
                    >
                      Core Charge: $
                      {parseFloat(finishedGood.coreCharge).toFixed(2)}
                    </MDTypography>
                  )}
                </MDBox>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Description */}
              {(finishedGood?.marketingDescription ||
                finishedGood?.description) && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      color: "#000000",
                      fontSize: "1.125rem",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Description
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: "#333333",
                      fontSize: "1rem",
                      lineHeight: 1.6,
                      letterSpacing: "0.01em",
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        finishedGood.marketingDescription ||
                        finishedGood.description,
                    }}
                  />
                </MDBox>
              )}

              {/* Unit Specific Details */}
              {finishedGood?.unitSpecificDetails && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      color: "#000000",
                      fontSize: "1.125rem",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Unit Specific Details
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: "#333333",
                      fontSize: "1rem",
                      lineHeight: 1.6,
                      letterSpacing: "0.01em",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: finishedGood.unitSpecificDetails,
                    }}
                  />
                </MDBox>
              )}

              {/* Technical Sections */}
              {Array.isArray(finishedGood?.technicalSections) &&
                finishedGood.technicalSections.length > 0 && (
                  <MDBox mb={3}>
                    <MDTypography
                      variant="h6"
                      fontWeight="bold"
                      mb={2}
                      sx={{
                        color: "#000000",
                        fontSize: "1.125rem",
                        letterSpacing: "0.01em",
                      }}
                    >
                      Technical Data
                    </MDTypography>
                    {finishedGood.technicalSections.map((sec, idx) => (
                      <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          {sec.title && (
                            <MDTypography
                              variant="subtitle1"
                              fontWeight="bold"
                              mb={1}
                            >
                              {sec.title}
                            </MDTypography>
                          )}
                          {sec.body && (
                            <MDTypography
                              variant="body2"
                              sx={{ color: "#333333" }}
                              dangerouslySetInnerHTML={{ __html: sec.body }}
                            />
                          )}
                          {sec.images?.length > 0 && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {sec.images.map((img) => {
                                const url = img?.attributes?.url || img?.url;
                                return (
                                  <Grid item xs={6} sm={4} md={3} key={url}>
                                    <Box
                                      component="img"
                                      src={url}
                                      alt={sec.title || "Technical image"}
                                      sx={{
                                        width: "100%",
                                        borderRadius: 1,
                                        objectFit: "cover",
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </MDBox>
                )}

              {/* Call to Action */}
              <MDBox mt={4}>
                <MDButton
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    bgcolor: BRAND.red,
                    color: "#ffffff",
                    fontSize: "1.125rem",
                    fontWeight: "bold",
                    letterSpacing: "0.02em",
                    py: 2,
                    mb: 2,
                    borderRadius: 2,
                    "&:hover": { bgcolor: "#B71C1C" },
                  }}
                  onClick={() => navigate("/authentication/sign-in")}
                >
                  Sign In to Order
                </MDButton>
                <MDButton
                  variant="outlined"
                  fullWidth
                  size="large"
                  sx={{
                    borderColor: BRAND.orange,
                    color: BRAND.orange,
                    fontSize: "1rem",
                    fontWeight: "medium",
                    letterSpacing: "0.02em",
                    py: 1.5,
                    borderWidth: 2,
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: BRAND.red,
                      borderWidth: 2,
                      color: BRAND.red,
                      bgcolor: "rgba(215, 38, 30, 0.06)",
                    },
                  }}
                  onClick={() => navigate("/catalog")}
                >
                  Continue Shopping
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <MDBox sx={{ bgcolor: "#000000", color: "#ffffff", py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <MDBox display="flex" alignItems="center" mb={2}>
                <MDBox
                  component="img"
                  src={brandLogo}
                  alt={brandName}
                  sx={{
                    height: 120,
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </MDBox>
              <MDTypography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  letterSpacing: "0.01em",
                }}
              >
                Smart Hydraulics. Smarter Shifts.
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDTypography
                variant="h6"
                fontWeight="bold"
                mb={2}
                sx={{
                  color: "#ffffff",
                  fontSize: "1.125rem",
                  letterSpacing: "0.01em",
                }}
              >
                Contact
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  letterSpacing: "0.01em",
                }}
              >
                For inquiries about our remanufacturing services or to place an
                order, please sign in to your account or contact us directly.
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDTypography
                variant="h6"
                fontWeight="bold"
                mb={2}
                sx={{
                  color: "#ffffff",
                  fontSize: "1.125rem",
                  letterSpacing: "0.01em",
                }}
              >
                Quick Links
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={1}>
                <MDButton
                  variant="text"
                  onClick={() => navigate("/catalog")}
                  sx={{
                    justifyContent: "flex-start",
                    color: "#ffffff",
                    "&:hover": { color: "#eb4b33" },
                  }}
                >
                  Catalog
                </MDButton>
                <MDButton
                  variant="text"
                  onClick={() => navigate("/")}
                  sx={{
                    justifyContent: "flex-start",
                    color: "#ffffff",
                    "&:hover": { color: "#eb4b33" },
                  }}
                >
                  Home
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
          <MDBox
            mt={4}
            pt={4}
            borderTop="1px solid rgba(255,255,255,0.1)"
            textAlign="center"
          >
            <MDTypography
              variant="body2"
              sx={{
                opacity: 0.8,
                color: "#ffffff",
                fontSize: "0.75rem",
                letterSpacing: "0.02em",
              }}
            >
              © {new Date().getFullYear()}{" "}
              {t("common.brandFallback", "Storefront")}.{" "}
              {t("common.footerRights", "All rights reserved.")}
            </MDTypography>
          </MDBox>
        </Container>
      </MDBox>
    </Box>
  );
}
